import { Info, X } from 'lucide-react';
import { useState } from 'react';

export function ModelSectionRenderer({ section, modelSlug }) {
    switch (section.type) {
        case 'imageGrid':
            return <ImageGrid section={section} />;

        case 'features':
            return <FeatureSection section={section} modelSlug={modelSlug} />;

        case 'specTable':
            return <SpecTable section={section} />;

        default:
            return null;
    }
}

function ImageGrid({ section }) {
    const gridClass =
        section.layout === 'masonry'
            ? 'grid grid-cols-2 md:grid-cols-4 gap-4'
            : 'grid grid-cols-1 md:grid-cols-2 gap-4';

    return (
        <section className="max-w-7xl mx-auto px-4 py-10">
            <div className={gridClass}>
                {section.images.map((src, i) => (
                    // Using a colored div as placeholder if image fails or for demo
                    <div key={i} className="relative overflow-hidden rounded-lg group aspect-[4/3]">
                        <img
                            src={src}
                            alt={`Gallery ${i}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://placehold.co/600x400'; // Fallback
                            }}
                        />
                    </div>
                ))}
            </div>
        </section>
    );
}

function FeatureSection({ section, modelSlug }) {
    const [activeFeature, setActiveFeature] = useState(null);
    const isSpecialModel = modelSlug === 'bj40-honor';

    return (
        <section className="max-w-7xl mx-auto px-4 py-16 bg-gray-50 my-8 rounded-xl relative">
            <h2 className="text-3xl font-bold mb-10 text-center">{section.title}</h2>

            <div className="grid md:grid-cols-2 gap-10">
                {section.items.map((item, i) => (
                    <div key={i} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                        <div className="h-64 overflow-hidden">
                            <img
                                src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://placehold.co/600x400';
                                }}
                            />
                        </div>
                        <div className="p-6">
                            <div className={isSpecialModel ? "flex items-center gap-2 mb-2" : "mb-2"}>
                                <h3 className={isSpecialModel ? "text-base md:text-xl font-bold" : "text-xl font-bold"}>
                                    {item.title}
                                </h3>
                                {isSpecialModel && (
                                    <button
                                        onClick={() => setActiveFeature(item)}
                                        className="md:hidden text-gray-500 hover:text-gray-700"
                                    >
                                        <Info size={18} />
                                    </button>
                                )}
                            </div>
                            <p className={`${isSpecialModel ? "hidden md:block" : ""} text-gray-600 leading-relaxed`}>
                                {item.description}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Mobile Feature Popup */}
            {activeFeature && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 md:hidden">
                    <div className="bg-white rounded-xl p-6 max-w-sm w-full relative shadow-xl animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setActiveFeature(null)}
                            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
                        >
                            <X size={24} />
                        </button>
                        <h3 className="text-xl font-bold mb-4 pr-8">{activeFeature.title}</h3>
                        <p className="text-gray-600 leading-relaxed text-sm">
                            {activeFeature.description}
                        </p>
                    </div>
                </div>
            )}
        </section>
    );
}

function SpecTable({ section }) {
    return (
        <section className="max-w-7xl mx-auto px-4 py-16">
            <h2 className="text-3xl font-bold mb-8 text-center">{section.title}</h2>

            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="w-full border-collapse bg-white text-sm md:text-base">
                    <thead className="bg-gray-900 text-white">
                        <tr>
                            <th className="text-left p-4 font-semibold uppercase tracking-wider">Specifications</th>
                            {section.columns.map(col => (
                                <th key={col} className="p-4 text-center font-semibold uppercase tracking-wider border-l border-gray-700">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                        {section.rows.map((row, i) => (
                            <tr key={i} className={`hover:bg-gray-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <td className="p-4 font-medium text-gray-900">{row.label}</td>
                                {row.values.map((val, idx) => (
                                    <td key={idx} className="p-4 text-center text-gray-600 border-l border-gray-200">
                                        {val}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
